import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';

/**
 * Hook: useDeletePasco
 * Deletes a PASCO record from the system
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for deleting PASCO
 * 
 * @example
 * ```typescript
 * const { mutate: deletePasco, isPending, error } = useDeletePasco({
 *     onSuccess: () => {
 *         console.log('PASCO deleted successfully');
 *     }
 * });
 * 
 * // Usage
 * deletePasco('pasco-123');
 * ```
 */
export function useDeletePasco(
    options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (pascoId: string) => {
            return await pascoService.delete(pascoId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate pasco list queries (but not specific pasco detail queries)
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) => query.queryKey.length === 1 // Only invalidate queries with exactly ['pascos'] key
            });

            // Remove the specific pasco from cache
            queryClient.removeQueries({ queryKey: ['pascos', variables] });

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
